import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, style, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả phù hợp"
        />
    );
};

const InitiativeManagementForm = ({
    isOpen, onClose, formData, setFormData, isEditing, readOnly,
    users = [], levels = [], solutionTypes = [],
    members = [], setMembers, toast, fetchData
}) => {

    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPendingFiles([]);
            setDeletedFileIds([]);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const existingFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try { return JSON.parse(formData.AttachmentUrl); } catch (e) { return []; }
    }, [formData.AttachmentUrl]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return { label: displayLabel, value: u.Id, searchKey: searchStr, fullName: fullName, email: email, dept: dept };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>{option.email} {option.dept ? ` - ${option.dept}` : ''}</span>
            </div>
        );
    };

    let currentUserInfo = null;
    try {
        currentUserInfo = JSON.parse(localStorage.getItem('user'));
    } catch (e) {
        console.error("Lỗi parse user từ localStorage:", e);
    }

    const isMemberLocked = readOnly;

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (readOnly) return;
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDateChange = (name, dateObj) => {
        if (readOnly) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString } });
    };

    const handleTitleBlur = async (e) => {
        if (readOnly) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 2) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;

            let userId = formData.UserId;
            if (!userId && members && members.length > 0) {
                userId = members[0].userId || members[0].Id;
            }
            if (!userId) userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isAdmin = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/initiativemanagement?userId=${userId}&isAdmin=${isAdmin}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.ApprovalDate ? formData.ApprovalDate.substring(0, 4) : '';
                const inputCert = formData.CertificateNumber ? formData.CertificateNumber.trim().toLowerCase() : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.Name);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.ApprovalDate ? item.ApprovalDate.substring(0, 4) : '';
                    const existingCert = item.CertificateNumber ? item.CertificateNumber.trim().toLowerCase() : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (inputCert && inputCert === existingCert)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện Sáng kiến rất giống đã tồn tại: "${item.Name}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const updateEvidenceFiles = (newFiles) => setFormData({ ...formData, AttachmentUrl: JSON.stringify(newFiles) });

    const handleFileDescChange = (index, desc) => {
        if (readOnly) return;
        const updated = [...existingFiles];
        updated[index].description = desc;
        updateEvidenceFiles(updated);
    };

    const handleMultiFileChange = (e) => {
        if (readOnly) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = files.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const removeExistingFile = (fileId) => {
        if (readOnly) return;
        setDeletedFileIds(prev => [...prev, fileId]);
        const updated = existingFiles.filter(f => f.fileId !== fileId && f.url !== fileId && f.link !== fileId && f.data !== fileId);
        updateEvidenceFiles(updated);
    };

    const removePendingFile = (index) => {
        if (readOnly) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const handleAddMember = () => {
        if (isMemberLocked) return;
        setMembers([...members, { userId: '', fullName: '', isMainAuthor: false }]);
    };

    const handleRemoveMember = (index) => {
        if (isMemberLocked) return;
        setMembers(members.filter((_, i) => i !== index));
    };

    const handleSetMainAuthor = (index) => {
        if (isMemberLocked) return;
        setMembers(members.map((m, i) => ({ ...m, isMainAuthor: i === index })));
    };

    const handleMemberSelect = (index, userId) => {
        if (isMemberLocked) return;
        const selectedUser = users.find(u => u.Id === parseInt(userId));
        const newMembers = [...members];
        if (selectedUser) {
            newMembers[index] = { userId: selectedUser.Id, fullName: `${selectedUser.LastName} ${selectedUser.FirstName}`, isMainAuthor: newMembers[index]?.isMainAuthor || false };
        } else {
            newMembers[index] = { userId: '', fullName: '', isMainAuthor: false };
        }
        setMembers(newMembers);
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadinitiativeevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL) {
            fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
            if (typeof fileUrl === 'string') {
                fileUrl = fileUrl.trim();
                if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                    fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
                }
            }
        }

        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        if (typeof fileUrl === 'string') {
            fileUrl = fileUrl.trim();
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;

        if (members.length === 0) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chọn ít nhất 1 thành viên!' });
            return;
        }

        let finalUserId = currentUserInfo?.Id || '';

        const mainAuthor = members.find(m => m.isMainAuthor === true);
        if (mainAuthor) {
            const authorId = mainAuthor.userId || mainAuthor.UserId || mainAuthor.Id || mainAuthor.id;
            if (authorId) {
                finalUserId = authorId;
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Tác giả chính chưa được chọn từ hệ thống!', life: 5000 });
                return;
            }
        } else {
            toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Vui lòng tích chọn một thành viên làm Tác giả chính!', life: 5000 });
            return;
        }

        setIsSubmitting(true);

        let cleanEvidence = [];
        try {
            const currentFiles = JSON.parse(formData.AttachmentUrl || '[]');
            cleanEvidence = currentFiles.filter(f => !f.fileId);
        } catch (err) { }

        const payload = {
            ...formData,
            UserId: parseInt(finalUserId),
            MembersJSON: JSON.stringify(members),
            AttachmentUrl: JSON.stringify(cleanEvidence),
            Id: isEditing ? formData.Id : 0
        };

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement`, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.status === 401 || res.status === 403) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
                return;
            }

            let result;
            try {
                result = await res.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi phản hồi từ máy chủ!' };
            }

            if (res.ok && result.status !== 'error') {
                const finalInitId = result.initiativeId || formData.SourceId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(async (fId) => {
                        const parsedId = parseInt(fId, 10);
                        if (!isNaN(parsedId)) {
                            const delRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/deleteinitiativeevidence?fileId=${parsedId}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                            });
                            if (!delRes.ok) console.error(`Lỗi xóa file cũ: ${parsedId}`);
                        }
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(async (pf) => {
                        const fd = new FormData();
                        fd.append('initiativeId', finalInitId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);

                        const uploadRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadinitiativeevidence`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
                            body: fd
                        });

                        if (!uploadRes.ok) {
                            const err = await uploadRes.json().catch(() => ({}));
                            throw new Error(err.message || `Upload file ${pf.name} thất bại!`);
                        }
                    }));
                }

                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Lưu dữ liệu thành công!' });
                if (typeof fetchData === 'function') fetchData(true);
                onClose();
            } else {
                const errorMsg = result.message || 'Lưu thất bại!';
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errorMsg, life: 6000 });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: e.message || 'Lỗi kết nối máy chủ!' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box" style={{ width: '90%', maxWidth: '1000px', maxHeight: '95vh', overflowY: 'auto' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '20px', lineHeight: '1.4' }}>
                        {isEditing ? (readOnly ? "Chi tiết Thông tin sáng kiến" : "Cập nhật Thông tin sáng kiến") : "Thêm mới Thông tin sáng kiến"}
                    </h3>
                    <button type="button" className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                </div>
                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="initiativeMngtForm" onSubmit={handleFormSubmit}>
                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label>Cấp sáng kiến *</label>
                                <select name="InitiativeLevel" className="form-input" value={formData.InitiativeLevel || ''} onChange={handleChange} disabled={readOnly} required>
                                    <option value="">Chọn cấp sáng kiến</option>
                                    {levels.map(l => <option key={l.Id} value={l.Name}>{l.Name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Loại giải pháp *</label>
                                <select name="SolutionType" className="form-input" value={formData.SolutionType || ''} onChange={handleChange} disabled={readOnly} required>
                                    <option value="">Chọn loại giải pháp</option>
                                    {solutionTypes.map(s => <option key={s.Id} value={s.Name}>{s.Name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Tên sáng kiến *</label>
                            <input
                                type="text"
                                name="Name"
                                className="form-input"
                                value={formData.Name || ''}
                                onChange={handleChange}
                                onBlur={handleTitleBlur}
                                disabled={readOnly}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Đơn vị chủ trì *</label>
                            <input type="text" name="HostInstitution" className="form-input" value={formData.HostInstitution || ''} onChange={handleChange} disabled={readOnly} required />
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày xét duyệt</label>
                                <Calendar
                                    id="ApprovalDate"
                                    name="ApprovalDate"
                                    value={(formData.ApprovalDate && !isNaN(new Date(formData.ApprovalDate).getTime())) ? new Date(formData.ApprovalDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('ApprovalDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isMemberLocked}
                                />
                            </div>
                            <div className="form-group">
                                <label>Chứng nhận số</label>
                                <input type="text" name="CertificateNumber" className="form-input" value={formData.CertificateNumber || ''} onChange={handleChange} disabled={readOnly} />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Tóm tắt nội dung sáng kiến</label>
                            <textarea name="Summary" className="form-input" rows="3" value={formData.Summary || ''} onChange={handleChange} disabled={readOnly}></textarea>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                                <label style={{ fontWeight: 'bold' }}>Các thành viên tham gia *</label>
                                <button type="button" className="btn-add-new" style={{ backgroundColor: isMemberLocked ? '#bdc3c7' : '#3498db', cursor: isMemberLocked ? 'not-allowed' : 'pointer', margin: 0 }} onClick={handleAddMember} disabled={isMemberLocked}>
                                    <i className="fa-solid fa-user-plus"></i> Chọn người dùng
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="custom-table" style={{ minWidth: '600px' }}>
                                    <thead style={{ backgroundColor: '#f9f9f9' }}>
                                        <tr>
                                            <th width="10%">STT</th>
                                            <th width="60%">Họ tên</th>
                                            <th width="20%" style={{ textAlign: 'center' }}>Tác giả chính</th>
                                            <th width="10%"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(!members || members.length === 0) ? (<tr><td colSpan="4" style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Chưa có thành viên nào</td></tr>) : members.map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                <td>
                                                    <SearchableFocusDropdown
                                                        value={m.userId}
                                                        options={userOptions}
                                                        onChange={(e) => handleMemberSelect(i, e.value)}
                                                        placeholder="Chọn thành viên"
                                                        panelUniqueClass={`custom-dropdown-panel-initiative-mngt-member-${i}`}
                                                        disabled={isMemberLocked}
                                                        itemTemplate={userItemTemplate}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input type="radio" name="mainAuthor" checked={m.isMainAuthor} onChange={() => handleSetMainAuthor(i)} disabled={isMemberLocked} />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {!isMemberLocked && <i className="fa-solid fa-trash-can" style={{ color: '#e74c3c', cursor: 'pointer' }} onClick={() => handleRemoveMember(i)}></i>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Tệp tin minh chứng</label>
                            {existingFiles.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="custom-table" style={{ width: '100%', marginBottom: '15px', border: '1px solid #eee', minWidth: '600px' }}>
                                        <thead>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                {!readOnly && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingFiles.map((file, idx) => (
                                                <tr key={`ext-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => handleFileDescChange(idx, e.target.value)}
                                                            disabled={readOnly}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">{file.name}</span>
                                                    </td>
                                                    {!readOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeExistingFile(file.fileId || file.url || file.data || file.link)}
                                                                style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {pendingFiles.length > 0 && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '600px', border: '1px solid #eee' }}>
                                        <thead style={{ backgroundColor: '#e8f6f3' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả (Tệp mới)</th>
                                                <th width="35%">Tên tệp</th>
                                                {!readOnly && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingFiles.map((file, idx) => (
                                                <tr key={`pend-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{existingFiles.length + idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => {
                                                                const newFiles = [...pendingFiles];
                                                                newFiles[idx].description = e.target.value;
                                                                setPendingFiles(newFiles);
                                                            }}
                                                            disabled={readOnly}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>
                                                            {file.name} <i className="fa-solid fa-cloud-arrow-up"></i>
                                                        </span>
                                                    </td>
                                                    {!readOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => removePendingFile(idx)}
                                                                style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isMemberLocked && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input type="file" multiple accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png" onChange={handleMultiFileChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }} title="Nhấn để chọn tệp" />
                                    <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                    <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Kéo thả hoặc nhấn để chọn tệp</p>
                                    <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                </div>
                            )}
                        </div>

                    </form>
                </div>
                <div className="modal-footer" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
                        {readOnly ? 'Đóng' : 'Hủy'}
                    </button>
                    {!readOnly && (
                        <button type="submit" form="initiativeMngtForm" className="btn-submit" disabled={isSubmitting}>
                            {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang xử lý...</> : 'Hoàn tất'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InitiativeManagementForm;